import VacancyCardCandidates from "./Candidates/VacancyCardCandidates";
import VacancyCardHistory from "./History/VacancyCardHistory";
import VacancyCardInfo from "./Info/VacancyCardInfo";
import SkeletonField from "./Skeleton/SkeletonField";
import style from "./VacancyCard.module.scss";
import { VacancyBadge, AdditionallyMenu } from "@/components";
import CreateCandidateModal from "@/components/BaseComponents/Popups/CreateCandidate/Modal";
import LinkWithVacancy from "@/components/BaseComponents/Popups/LinkWithVacancy/Modal";
import MassEffectModal from "@/components/BaseComponents/Popups/MassEfffect/Modal";
import {
  ROUTE_CREATE_CANDIDATE,
  ROUTE_VACANCIES_UPDATE,
} from "@/constants/routes";
import { useVacationCardQuery } from "@/core/api/vacancy/vacancy";
import { useUserStore } from "@/core/store/UserDataStore";
import useOnMountUnmount from "@/hooks/useOnMountUnmoun";
import { Badge, Headline, Tab, Tabs } from "@aurora/components";
import {
  GlyphBetweenAccounts,
  GlyphEdit,
  GlyphMore,
  GlyphPlus,
  GlyphSurvey,
  GlyphSwap,
} from "@aurora/icons";
import cx from "classnames";
import { FC, useCallback, useState, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

interface Props {
  vacancy?: any;
  openUpdateStatus?: any;
}

const VacancyCard: FC<Props> = ({ openUpdateStatus, vacancy }: any) => {
  const { isManagerOrWatcher } = useUserStore();
  const MemoTabs = memo(Tabs);

  const [isShowVacancyPopup, setShowVacancyPopup] = useState(false);
  const [isShowMassEffectPopup, setShowMassEffect] = useState(false);
  const [_sp, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const [activeTab, changeActiveTab] = useState(0);
  const [isOpenAddCandidateModal, changeIsOpenAddCandidateModal]: any =
    useState(false);
  const [refreshCandidateList, setRefreshCandidateList] = useState(false);

  const [queryParams, changeQueryParams] = useState({
    timestamp: new Date().getTime(),
    vacancyId: vacancy.id,
  });

  const { data: cardData, isLoading: cardDataIsLoading } =
    useVacationCardQuery(queryParams);

  const handleChangeTab = useCallback(
    (event: any, value: any) => {
      changeActiveTab(value);
    },
    [changeActiveTab],
  );

  function updateVacancyCardData() {
    changeQueryParams(prev => ({
      ...prev,
      ts: new Date().getTime(),
    }));
  }

  useOnMountUnmount({
    onMount: () => {
      setSearchParams({ vacancyId: vacancy.id });
    },
  });

  return (
    <>
      {cardDataIsLoading ? (
        <SkeletonField count={8} />
      ) : (
        <div className={style.VacationCard}>
          <div className={style.VacationCardHeader}>
            <div>
              <div className={style.BadgeContainer}>
                {cardData && <VacancyBadge badge={cardData as IBadge} />}
                {cardData?.isPublic && (
                  <Badge
                    className={cx(style.VacationBadge, "truncate-cell-percent")}
                    color={"success"}
                    label={"Для сайта"}
                    size={"m28"}
                    theme={"light"}
                    variant={"fh-v2"}
                  />
                )}
              </div>
              <Headline
                addonContent={
                  cardData &&
                  cardData.positionTotalCount > 0 && (
                    <Badge
                      color={"alert"}
                      label={
                        cardData?.positionClosedCount +
                        "/" +
                        cardData?.positionTotalCount
                      }
                      shape={"pill"}
                      size={"l32"}
                      variant={"primary"}
                    />
                  )
                }
                positionAddonContent={"right"}
                title={cardData?.title}
              />
            </div>
            {isManagerOrWatcher ? (
              ""
            ) : (
              <div className={style.VacationCardHeaderButtons}>
                <AdditionallyMenu
                  btn={{
                    className: cx(style.CreateButton, "pr-12"),
                    icon: <GlyphPlus />,
                    name: "Добавить кандидата",
                    shape: "circle",
                    variant: "primary",
                  }}
                  list={[
                    {
                      icon: <GlyphBetweenAccounts />,
                      name: "Загрузить резюме",
                      onClick: () => {
                        changeIsOpenAddCandidateModal(true);
                      },
                    },
                    {
                      icon: <GlyphSurvey />,
                      name: "Добавить из списка",
                      onClick: () => {
                        setShowVacancyPopup(true);
                      },
                    },
                    {
                      icon: <GlyphPlus />,
                      name: "Создать кандидата",
                      onClick: () => {
                        navigate(
                          `${ROUTE_CREATE_CANDIDATE}?vacancyId=${vacancy.id}`,
                        );
                      },
                    },
                  ]}
                />
                <AdditionallyMenu
                  btn={{
                    className: cx(style.CreateButton, "text-black"),
                    icon: <GlyphMore />,
                    variant: "secondary",
                  }}
                  list={[
                    {
                      icon: <GlyphEdit />,
                      name: "Редактировать",
                      onClick: () => {
                        navigate(`${ROUTE_VACANCIES_UPDATE}/${cardData?.id}`);
                      },
                    },
                    {
                      icon: <GlyphSwap />,
                      name: "Перевод статуса",
                      onClick: () => {
                        openUpdateStatus(() => updateVacancyCardData);
                      },
                    },
                    // FORK-698: Front. Убрать кнопку Массовые действия
                    // {
                    //   icon: <GlyphPeople />,
                    //   name: "Массовое действие",
                    //   onClick: () => {
                    //     setShowMassEffect(true);
                    //   },
                    // },
                  ]}
                />
              </div>
            )}
          </div>
          <div className="mb-8">
            <MemoTabs
              onChange={handleChangeTab}
              value={activeTab}
              variant="primary"
            >
              <Tab key="0" label="Описание" value={0} />
              <Tab key="1" label="Кандидаты" value={1} />
              <Tab key="2" label="История" value={2} />
            </MemoTabs>
          </div>
          {activeTab === 0 && <VacancyCardInfo vacancyId={vacancy.id} />}
          {activeTab === 1 && (
            <>
              <VacancyCardCandidates
                refreshList={refreshCandidateList}
                vacancy={vacancy}
              />
            </>
          )}
          {activeTab === 2 && (
            <>
              <VacancyCardHistory
                stateCode={cardData?.stateCode as string}
                vacancyId={cardData?.id as string}
              />
            </>
          )}
          {isOpenAddCandidateModal ? (
            <CreateCandidateModal
              clickOnClose={() => {
                changeIsOpenAddCandidateModal(false);
              }}
              onSuccessModal={() => {
                setRefreshCandidateList(true);
                setTimeout(() => setRefreshCandidateList(false), 500);
              }}
              vacancy={cardData?.id}
            />
          ) : null}
          {isShowVacancyPopup ? (
            <LinkWithVacancy
              clickOnClose={() => {
                setShowVacancyPopup(false);
                setRefreshCandidateList(true);
                setTimeout(() => setRefreshCandidateList(false), 500);
              }}
              type="vacancy"
              vacancy={{ ...vacancy, ...cardData }}
            />
          ) : null}
          {isShowMassEffectPopup && (
            <MassEffectModal
              clickOnClose={() => {
                setShowMassEffect(false);
                updateVacancyCardData();
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
export default VacancyCard;
